import React from 'react';

import { Skeleton, Tag } from 'antd';
import { DotLoading, InfiniteScroll } from 'antd-mobile';
import { sleep } from 'antd-mobile/es/utils/sleep';
import {
  Checkbox,
  CheckboxGroup,
  Column,
  Row,
  Space,
  Text,
} from 'farmhub-ui-core';

import { useAppDispatch, useAppSelector } from 'app';
import { dataEmpty, Empty, ShortFilterTable } from 'components';
import { Line } from 'containers/WarehouseOperation/WorkSpace/components';
import { findProductsToConnect, selectProductUnit } from 'slice';
import { theme } from 'styled';
import {
  buildProductWholesaleUnit,
  formatImageUrl,
  formatMoney,
  getDummyArray,
  getRelativeQuantityColor,
  IMAGETYPE,
  reportQuantity,
  SIZE,
} from 'utils';

import {
  CTag,
  Image,
  SkeletonStyles,
  WrapCheckBox,
  WrapCheckBoxParent,
  WrapList,
  WrapListParent,
} from './styles';

const productItemSkeleton = (): JSX.Element => (
  <Row styles={{ gap: 12 }} width="100%">
    <Skeleton.Avatar
      shape="square"
      style={{
        width: 48,
        height: 48,
        borderRadius: 6,
      }}
    />
    <Column styles={SkeletonStyles} width="70%">
      <Space height={2} />
      <Skeleton.Input size="small" style={{ width: '80%', height: 16 }} />
      <Skeleton.Input size="small" style={{ width: '100%', height: 16 }} />
    </Column>
    <Column styles={SkeletonStyles} width="50%">
      <Skeleton.Input
        size="small"
        style={{ width: '70%', height: 16, float: 'right', borderRadius: 3 }}
      />
      <Space height={2} />
      <Skeleton.Input
        size="small"
        style={{ width: '70%', height: 16, float: 'right', borderRadius: 3 }}
      />
    </Column>
  </Row>
);

interface ProductStepDesktopProps {
  myType?: string;
  supplierID?: string;
  dataFilter?: any;
  handleFilterFormChange?: (e: any) => void;
  selectedProducts: string[];
  setSelectedProducts: (value: string[]) => void;
  selectAllProducts: boolean;
  setSelectAllProducts: (value: boolean) => void;
  setTotalProduct: (value: number) => void;
  totalProduct: number;
  filterForm?: any;
  query?: any;
  setQuery: (e: any) => void;
}

const paginationInit = {
  current: 1,
  next: 2,
  prev: 0,
  take: 20,
  total: 0,
};

const ProductStepMobile = ({
  myType,
  supplierID,
  dataFilter,
  handleFilterFormChange,
  selectedProducts,
  setSelectedProducts,
  selectAllProducts,
  setSelectAllProducts,
  setTotalProduct,
  totalProduct,
  filterForm,
  query,
  setQuery,
}: ProductStepDesktopProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = React.useState(true);
  const [products, setProducts] = React.useState<any[]>([]);
  const productUnit = useAppSelector(selectProductUnit);
  const [pagination, setPagination] = React.useState(paginationInit);
  const handleSelectProduct = (selectedRowKeys: any[]): void => {
    setSelectedProducts(selectedRowKeys as string[]);
  };

  const handleSelectAllProductsTags = (checked: any): void => {
    setSelectAllProducts(checked);
    if (!checked) {
      setSelectedProducts([]);
    }
  };

  const getSelectAllTitle = (): string => {
    if (selectAllProducts) {
      return `Bỏ chọn tất cả ${totalProduct} sản phẩm`;
    }
    return `Chọn tất cả ${totalProduct} sản phẩm`;
  };

  React.useEffect(() => {
    const getInvitationProducts = async (): Promise<void> => {
      setIsLoading(true);

      const options = {
        order: { createdAt: 0 },
        entityID: supplierID,
        status: 0,
        name: query.name,
        entityCategoryID: query.entityCategoryID,
        page: pagination.current,
        take: pagination.take,
      };

      try {
        const result: any = await dispatch(
          findProductsToConnect(options)
        ).unwrap();

        const { data, pagination: paginationResult } = result;
        setPagination(paginationResult);
        setTotalProduct(paginationResult.total);
        const formattedList = data.map((item: any) => {
          return {
            ...item.product,
            key: item.product.id,
          };
        });
        if (pagination.current === 1 && query?.name !== '') {
          setProducts(formattedList);
        } else if (pagination.current == 1) {
          setProducts(formattedList);
        } else {
          setProducts(prev => [...prev, ...formattedList]);
        }
      } catch (error) {
        console.log(
          'An error occurred while getting invitation products',
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    getInvitationProducts();
  }, [query, myType, supplierID, pagination.current]);

  const handleChecklist = (id: string): void => {
    if (selectedProducts?.length === 0) {
      handleSelectProduct([id]);
    } else {
      if (!selectedProducts.includes(id)) {
        handleSelectProduct([...selectedProducts, id]);
      } else {
        handleSelectProduct(selectedProducts.filter(item => item != id));
      }
    }
  };

  const loadMore = async (): Promise<void> => {
    await sleep(2000);
    setPagination(prevState => {
      return {
        ...prevState,
        current: pagination.current + 1,
        take: prevState.take,
      };
    });
  };

  return (
    <Column width="100%">
      <Space height={36} />
      <ShortFilterTable
        data={dataFilter}
        form={filterForm}
        handleFilterFormChange={e => {
          handleFilterFormChange && handleFilterFormChange(e);
          setPagination(prev => {
            return {
              ...prev,
              current: 1,
            };
          });
        }}
        name="sản phẩm"
        searchName="name"
        searchPlaceholder="Tìm kiếm sản phẩm"
        searchWidth="calc(100vw - (180px + 36px))"
        size={SIZE.SMALL}
      />
      <Space height={12} />
      {totalProduct > 0 && selectAllProducts != null && (
        <WrapCheckBox>
          <Text weight="bold">
            Sản phẩm{' '}
            {!selectAllProducts && selectedProducts.length > 0 && (
              <>(đã chọn {selectedProducts.length})</>
            )}
          </Text>
          <WrapCheckBoxParent>
            {products.length > 0 && (
              <Tag.CheckableTag
                checked={selectAllProducts}
                style={{
                  width: 200,
                  background: theme.colors.dropdown.main,
                  color: theme.colors.primary.main,
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 6,
                  textAlign: 'center',
                  margin: 0,
                }}
                onChange={handleSelectAllProductsTags}
              >
                {getSelectAllTitle()}
              </Tag.CheckableTag>
            )}
          </WrapCheckBoxParent>
        </WrapCheckBox>
      )}
      <Space height={18} />
      <WrapListParent>
        <WrapList>
          {isLoading && products.length === 0 ? (
            getDummyArray(10).map((item, index: number) => (
              <>
                <Row key={item.key}>{productItemSkeleton()}</Row>
                {index < 9 && <Space height={6} />}
              </>
            ))
          ) : products.length === 0 ? (
            <Empty
              description="Hiện tại không có sản phẩm"
              img={dataEmpty.CATEGORY}
            />
          ) : (
            <>
              <CheckboxGroup
                styles={{ width: '100%' }}
                value={selectedProducts}
              >
                {products?.map((item: any) => (
                  <>
                    <Row>
                      <Row styles={{ padding: '6px 0' }} width="100%">
                        {!selectAllProducts && (
                          <>
                            <Checkbox
                              key={item.id}
                              value={item.id}
                              onChange={() => handleChecklist(item.id)}
                            />
                            <Space width={12} />
                          </>
                        )}
                        <Image
                          src={formatImageUrl(
                            item.avatar,
                            IMAGETYPE.PRODUCT.xxs
                          )}
                        />
                        <Space width={12} />
                        <Column>
                          <Text
                            color="primary"
                            lineHeight="22px"
                            lineNumber={1}
                            styles={{ fontSize: 13 }}
                            weight="bold"
                          >
                            {item.name}
                          </Text>
                          <Row>
                            <Text
                              color="primary"
                              styles={{ whiteSpace: 'nowrap' }}
                            >
                              Số lượng:
                            </Text>
                            <Text
                              styles={{
                                marginLeft: 3,
                                color: getRelativeQuantityColor(item),
                                fontSize: 13,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {reportQuantity(item)}
                            </Text>
                          </Row>
                        </Column>
                      </Row>
                      <Column
                        alignItems="flex-end"
                        justifyContent="flex-end"
                        styles={{ gap: 6, padding: '6px 0' }}
                        width="50%"
                      >
                        {(item?.wholesalePriceUnit || []).map(
                          (priceUnit: any) => (
                            <CTag key={priceUnit?.id}>
                              {formatMoney(priceUnit?.price)}đ/
                              {buildProductWholesaleUnit({
                                unitPrefix:
                                  productUnit.prefixObj[priceUnit?.prefix],
                                unitWeight: priceUnit?.weight,
                                unitPostfix:
                                  productUnit.postfixObj[priceUnit?.postfix],
                              })}
                            </CTag>
                          )
                        )}
                      </Column>
                    </Row>
                    <Line
                      color="#d9d9d9"
                      height="1px"
                      margin="0"
                      vertical={false}
                    />
                  </>
                ))}
              </CheckboxGroup>
              {isLoading && (
                <>
                  <Space height={12} />
                  {getDummyArray(5).map(item => (
                    <Row key={item.key}>{productItemSkeleton()}</Row>
                  ))}
                </>
              )}
            </>
          )}
          <InfiniteScroll
            hasMore={products?.length < totalProduct}
            loadMore={loadMore}
          >
            {products?.length < totalProduct && (
              <>
                <span>Đang tải</span>
                <DotLoading />
              </>
            )}
          </InfiniteScroll>
          {!isLoading && <Space height={50} />}
        </WrapList>
        <Space height={30} />
      </WrapListParent>
    </Column>
  );
};

export default ProductStepMobile;
